import { MapPin, Phone, Navigation, Instagram, MessageCircle } from "lucide-react";
import { Reveal, MaskLine, Eyebrow } from "./motion";
import { RotatingBadge, LogoMark } from "./Logo";
import { waLink, MAPS_LINK, IG_LINK, IMG, ADDRESS, PHONE_1, PHONE_2, TEL_1, TEL_2, IG_HANDLE } from "./data";

export default function Visit() {
    return (
        <section id="visit" data-testid="visit-section" className="bg-cream-ivory py-24 text-ink lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="max-w-3xl">
                    <Reveal>
                        <Eyebrow className="!text-[#A97F1C]">Find The Store</Eyebrow>
                    </Reveal>
                    <h2 className="mt-4 font-display text-4xl font-bold leading-[1.02] tracking-tight text-maroon-900 sm:text-5xl lg:text-6xl">
                        <MaskLine>Cooper Ganj,</MaskLine>
                        <MaskLine delay={0.1} className="italic font-light text-[#A97F1C]">
                            Kanpur.
                        </MaskLine>
                    </h2>
                    <Reveal delay={0.2}>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/65 sm:text-lg">
                            Walk in, see the stock, take your rates — or start the conversation on
                            WhatsApp first.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
                    {/* visiting card */}
                    <Reveal className="lg:col-span-6">
                        <div
                            data-testid="visit-address-card"
                            className="relative rounded-[2rem] bg-maroon-950 p-8 text-cream shadow-[0_40px_80px_-40px_rgba(38,6,11,0.6)] sm:p-10"
                        >
                            <div className="pointer-events-none absolute inset-2.5 rounded-[1.6rem] border border-gold/25" aria-hidden="true" />
                            <div className="relative">
                                <div className="flex items-center gap-4">
                                    <LogoMark size={52} />
                                    <div>
                                        <p className="font-display text-xl font-bold tracking-[0.12em]">BABA GARMENTS</p>
                                        <p className="font-display text-sm italic text-gold-light">
                                            Complete Family Fashion Store
                                        </p>
                                    </div>
                                </div>

                                <div className="my-7 h-px bg-gold/15" aria-hidden="true" />

                                <div className="flex items-start gap-3.5">
                                    <MapPin size={20} className="mt-1 shrink-0 text-gold" />
                                    <p className="text-lg leading-relaxed text-cream/85" data-testid="visit-address-text">
                                        {ADDRESS}
                                    </p>
                                </div>

                                <div className="mt-6 space-y-3">
                                    <a
                                        href={TEL_1}
                                        data-testid="visit-call-1"
                                        className="flex items-center justify-between gap-3 rounded-2xl border border-gold/20 px-5 py-4 transition-colors duration-300 hover:border-gold/60 hover:bg-gold/10"
                                    >
                                        <span className="flex items-center gap-3 text-sm text-cream/70">
                                            <Phone size={17} className="text-gold" />
                                            Call for wholesale rates
                                        </span>
                                        <span className="font-display text-lg font-semibold text-cream sm:text-xl">
                                            {PHONE_1}
                                        </span>
                                    </a>
                                    <a
                                        href={TEL_2}
                                        data-testid="visit-call-2"
                                        className="flex items-center justify-between gap-3 rounded-2xl border border-gold/20 px-5 py-4 transition-colors duration-300 hover:border-gold/60 hover:bg-gold/10"
                                    >
                                        <span className="flex items-center gap-3 text-sm text-cream/70">
                                            <Phone size={17} className="text-gold" />
                                            Call for wholesale rates
                                        </span>
                                        <span className="font-display text-lg font-semibold text-cream sm:text-xl">
                                            {PHONE_2}
                                        </span>
                                    </a>
                                </div>

                                <div className="mt-7 flex flex-wrap gap-3">
                                    <a
                                        href={MAPS_LINK}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="visit-map-button"
                                        className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3.5 text-[13px] font-bold uppercase tracking-[0.14em] text-maroon-950 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
                                    >
                                        <Navigation size={16} />
                                        Open in Google Maps
                                    </a>
                                    <a
                                        href={waLink()}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="visit-whatsapp-button"
                                        className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                                    >
                                        <MessageCircle size={16} />
                                        WhatsApp for Rates
                                    </a>
                                    <a
                                        href={IG_LINK}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="visit-instagram-link"
                                        className="inline-flex items-center gap-2 rounded-full px-4 py-3.5 text-[13px] font-semibold text-gold transition-colors duration-300 hover:text-gold-pale"
                                    >
                                        <Instagram size={16} />
                                        {IG_HANDLE}
                                    </a>
                                </div>

                                <p className="mt-6 text-xs uppercase tracking-[0.22em] text-gold-light/70" data-testid="visit-wholesale-note">
                                    Strictly wholesale — retailers &amp; bulk buyers only
                                </p>
                            </div>
                        </div>
                    </Reveal>

                    {/* store visual */}
                    <Reveal delay={0.12} className="lg:col-span-6">
                        <div className="relative h-full">
                            <RotatingBadge
                                text="BABA GARMENTS • COOPER GANJ •"
                                icon={<MapPin size={22} />}
                                className="absolute -right-7 -top-7 z-20 hidden h-28 w-28 rounded-full bg-cream text-[#A97F1C] shadow-xl ring-1 ring-maroon-900/15 lg:block"
                            />
                            <div className="relative h-full rounded-[2rem] border border-maroon-900/15 bg-maroon-950 p-2">
                                <div className="relative aspect-[4/5] h-full max-h-full w-full overflow-hidden rounded-[1.6rem] lg:aspect-auto">
                                    <img
                                        src={IMG.store}
                                        alt="Neatly stacked fabrics ready for wholesale buyers at Baba Garments"
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                                    />
                                    <div
                                        className="pointer-events-none absolute inset-0"
                                        style={{
                                            background:
                                                "linear-gradient(180deg, rgba(38,6,11,0) 45%, rgba(38,6,11,0.9) 100%)",
                                        }}
                                        aria-hidden="true"
                                    />
                                    <div className="absolute inset-x-5 bottom-5 flex items-center gap-3 rounded-2xl glass-dark border border-gold/25 px-5 py-4" data-testid="visit-landmark-chip">
                                        <MapPin size={18} className="shrink-0 text-gold" />
                                        <p className="text-sm leading-snug text-cream/85">
                                            Landmark: <span className="font-semibold text-cream">Near Hamraj Complex</span>, Cooper Ganj
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* CTA band */}
                <Reveal delay={0.15}>
                    <div
                        data-testid="visit-cta-band"
                        className="mt-16 flex flex-col items-start justify-between gap-6 rounded-[2rem] bg-gold p-8 text-maroon-950 sm:p-12 md:flex-row md:items-center"
                    >
                        <h3 className="max-w-xl font-display text-2xl font-bold leading-snug sm:text-4xl">
                            Wholesale rates &amp; catalogue —{" "}
                            <span className="italic font-light">one message away.</span>
                        </h3>
                        <a
                            href={waLink()}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="visit-cta-band-button"
                            className="group inline-flex shrink-0 items-center gap-2.5 rounded-full bg-maroon-950 px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-cream shadow-[0_16px_40px_-16px_rgba(38,6,11,0.8)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-maroon-800"
                        >
                            <MessageCircle size={18} className="transition-transform duration-300 group-hover:-rotate-12" />
                            Start on WhatsApp
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}